import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, CalendarProps, DatePicker } from '@progress/kendo-react-dateinputs';
import type { ApiScheduleGroupRow, WorkOrdersInput, WorkOrdersComplianceSettings } from '../Components/WorkOrders/WorkOrdersTypes';
import { Button, ButtonHandle } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { filterIcon, chevronDownIcon, graphIcon, arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import { Drawer, DrawerContent } from '@progress/kendo-react-layout';
import { Popup } from '@progress/kendo-react-popup';
import { Loader } from '@progress/kendo-react-indicators';
import { useLocalization } from '@progress/kendo-react-intl';
import moment from 'moment-timezone';
import { useQuery } from 'react-query';
import { ReactQueryKeys } from '../../../Constants/ClientRoutingConstants';
import areasApiService from '../../Area/Utilities/AreasApiService';
import machinesApiService from '../../Assets/Utilities/MachinesApiService';
import schedulesApiService from '../../Schedules/Utilities/ScheduleApiService';
import tasksApiService from '../../Tasks/Utilities/TasksApiService';
import LoadingSpinner from '../../Shared/Components/LoadingSpinner';
import WorkOrders from '../Components/WorkOrders/WorkOrders';
import { ComplianceFilters } from '../Components/WorkOrders/WorkOrdersFilters';
import { WorkOrdersFilterPanelItem } from '../Components/WorkOrders/WorkOrdersFilterPanel';
import WorkOrdersLegend from '../Components/WorkOrders/WorkOrdersLegend';
import { EventFilter } from '../../Events/Utilities/EventsApiService';
import { enMessages } from '../../../messages/en-US';
import { useAccountSettingsStore } from '../../../Zustand/accountSettingsStore';
import {
  REPORT_SCHEDULE_SELECT_ALL_ROW,
  NO_SCHEDULE_ID,
  allItemsFromFlatTree,
  buildScheduleReportTree,
  schedulesForSelectedAssets,
  buildLocationAssetTree,
  assetIdsFromTreeSelection,
  allItemsFromNestedTree,
} from '../Utilities/workOrdersReportFilterCascade';
import workOrdersReportApiService from '../Utilities/WorkOrdersReportApiService';

/** Rows without a named schedule (API may send blank or "No Schedule"). */
function isWorkOrdersNoScheduleRow(row: Pick<ApiScheduleGroupRow, 'scheduleName'>): boolean {
  const n = (row.scheduleName ?? '').trim();
  return n === '' || n.toLowerCase() === 'no schedule';
}

// ─── Filter hook ──────────────────────────────────────────────────────────────

function useWorkOrdersFilters(rootSelectAllLabel: string) {
  const areasQuery = useQuery(ReactQueryKeys.AllAreasQuery, () => areasApiService.getAllAreas());
  const machinesQuery = useQuery(ReactQueryKeys.AllMachinesQuery, () => machinesApiService.getAllMachines());
  const schedulesQuery = useQuery(ReactQueryKeys.AllSchedulesQuery, () => schedulesApiService.getAllSchedules());
  const tasksQuery = useQuery(ReactQueryKeys.AllTasksQuery, () => tasksApiService.getAllTasks());

  /** All schedules linked (via tasks) to any asset — the full Schedule filter list. */
  const allSchedulesForTree = useMemo(() => {
    if (!schedulesQuery.data || !tasksQuery.data || !machinesQuery.data) return [];
    const allAssetIds = new Set<string>(machinesQuery.data.map((m: any) => String(m.id)));
    return schedulesForSelectedAssets(tasksQuery.data, schedulesQuery.data, allAssetIds);
  }, [schedulesQuery.data, tasksQuery.data, machinesQuery.data]);

  const scheduleTree = useMemo(
    () => buildScheduleReportTree(rootSelectAllLabel, allSchedulesForTree),
    [rootSelectAllLabel, allSchedulesForTree]
  );

  /** Location → Asset tree, cascading with the Schedule filter. */
  const locationAssetTree = useMemo(
    () => buildLocationAssetTree(rootSelectAllLabel, areasQuery.data ?? [], machinesQuery.data ?? []),
    [rootSelectAllLabel, areasQuery.data, machinesQuery.data]
  );

  const [displayLocAssetTree, setDisplayLocAssetTree] = useState<any[]>([]);
  const [selectedLocAssetTree, setSelectedLocAssetTree] = useState<any[]>([]);
  const [displayScheduleTree, setDisplayScheduleTree] = useState<any[]>([]);
  const [selectedSchedules, setSelectedSchedules] = useState<any[]>([]);
  const [includeUntrackedGreasing, setIncludeUntrackedGreasing] = useState(true);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const schedulesInitialized = useRef(false);
  useEffect(() => { schedulesInitialized.current = false; }, [locationAssetTree]);

  useEffect(() => {
    if (locationAssetTree[0]?.items?.length > 0) {
      setDisplayLocAssetTree(locationAssetTree);
      setSelectedLocAssetTree(allItemsFromNestedTree(locationAssetTree));
    }
  }, [locationAssetTree]);

  useEffect(() => {
    if (allSchedulesForTree.length > 0 && !schedulesInitialized.current) {
      schedulesInitialized.current = true;
      setDisplayScheduleTree(scheduleTree);
      setSelectedSchedules(allItemsFromFlatTree(scheduleTree));
    }
  }, [allSchedulesForTree, scheduleTree]);

  // Location→Asset tree drives the Schedule filter: narrow schedules to those linked to selected assets.
  const handleLocAssetTreeChange = useCallback((data: any[]) => {
    setSelectedLocAssetTree(data);
    const assetIds = assetIdsFromTreeSelection(data);
    const newSchedules = schedulesForSelectedAssets(tasksQuery.data ?? [], schedulesQuery.data ?? [], assetIds);
    const narrowed = buildScheduleReportTree(rootSelectAllLabel, newSchedules);
    setDisplayScheduleTree(narrowed);
    setSelectedSchedules(allItemsFromFlatTree(narrowed));
  }, [rootSelectAllLabel, tasksQuery.data, schedulesQuery.data]);

  // Schedule filter drives the tree: narrow the Location→Asset tree to assets linked to selected schedules.
  const handleSchedulesChange = useCallback((data: any[]) => {
    setSelectedSchedules(data);
    const includesNoSchedule = data.some((s: any) => s.id === NO_SCHEDULE_ID);
    if (includesNoSchedule) {
      setDisplayLocAssetTree(locationAssetTree);
      setSelectedLocAssetTree(allItemsFromNestedTree(locationAssetTree));
      return;
    }
    const scheduleIds = new Set<string>(
      data.filter((s: any) => s.id !== REPORT_SCHEDULE_SELECT_ALL_ROW.id && s.id !== NO_SCHEDULE_ID).map((s: any) => String(s.id))
    );
    const machineIds = new Set<string>(
      (tasksQuery.data ?? [])
        .filter((t: any) => t.scheduleId && scheduleIds.has(String(t.scheduleId)) && t.machineId)
        .map((t: any) => String(t.machineId))
    );
    const matchedMachines = (machinesQuery.data ?? []).filter((m: any) => machineIds.has(String(m.id)));
    const narrowed = buildLocationAssetTree(rootSelectAllLabel, areasQuery.data ?? [], matchedMachines);
    setDisplayLocAssetTree(narrowed);
    setSelectedLocAssetTree(allItemsFromNestedTree(narrowed));
  }, [rootSelectAllLabel, tasksQuery.data, machinesQuery.data, areasQuery.data, locationAssetTree]);

  const handleResetFilters = useCallback(() => {
    setDisplayLocAssetTree(locationAssetTree);
    setSelectedLocAssetTree(allItemsFromNestedTree(locationAssetTree));
    setDisplayScheduleTree(scheduleTree);
    setSelectedSchedules(allSchedulesForTree.length > 0 ? allItemsFromFlatTree(scheduleTree) : []);
    setIncludeUntrackedGreasing(true);
    setFilterResetKey((k) => k + 1);
  }, [locationAssetTree, scheduleTree, allSchedulesForTree]);

  /** Restore the filters to a previously captured snapshot (used to discard unapplied edits on close). */
  const restoreFilters = useCallback((snap: {
    displayLocAssetTree: any[]; selectedLocAssetTree: any[];
    displayScheduleTree: any[]; selectedSchedules: any[]; includeUntrackedGreasing: boolean;
  }) => {
    setDisplayLocAssetTree(snap.displayLocAssetTree);
    setSelectedLocAssetTree(snap.selectedLocAssetTree);
    setDisplayScheduleTree(snap.displayScheduleTree);
    setSelectedSchedules(snap.selectedSchedules);
    setIncludeUntrackedGreasing(snap.includeUntrackedGreasing);
    setFilterResetKey((k) => k + 1);
  }, []);

  /** Location→Asset tree: count selected asset leaves vs total assets. */
  const locAssetTreeTagCounts = useMemo(() => ({
    selected: assetIdsFromTreeSelection(selectedLocAssetTree).size,
    total: assetIdsFromTreeSelection(allItemsFromNestedTree(displayLocAssetTree)).size,
  }), [selectedLocAssetTree, displayLocAssetTree]);

  const scheduleTagCounts = useMemo(() => ({
    selected: selectedSchedules.filter((s: any) => s.id !== REPORT_SCHEDULE_SELECT_ALL_ROW.id).length,
    total: displayScheduleTree[0]?.items?.length ?? 0,
  }), [selectedSchedules, displayScheduleTree]);

  const filtersLoading = areasQuery.isLoading || areasQuery.isFetching
    || machinesQuery.isLoading || machinesQuery.isFetching
    || schedulesQuery.isLoading || schedulesQuery.isFetching
    || tasksQuery.isLoading || tasksQuery.isFetching;

  return {
    allLocAssetTree: displayLocAssetTree,
    selectedLocAssetTree,
    onSelectedLocAssetTreeChange: handleLocAssetTreeChange,
    locAssetTreeDisabled: filtersLoading,
    locAssetTreeTagCounts,
    allSchedules: displayScheduleTree,
    selectedSchedules,
    onSelectedSchedulesChange: handleSchedulesChange,
    schedulesDisabled: schedulesQuery.isLoading || schedulesQuery.isFetching || tasksQuery.isLoading || tasksQuery.isFetching,
    scheduleTagCounts,
    includeUntrackedGreasing,
    onIncludeUntrackedGreasingChange: setIncludeUntrackedGreasing,
    handleResetFilters, filterResetKey, filtersLoading, restoreFilters,
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const CustomCalendar = (props: CalendarProps) => <Calendar {...props} navigation={false} />;

export default function WorkOrdersReportPage() {
  const localizationService = useLocalization();
  const timezone = useAccountSettingsStore((state) => state.accountTimezone);

  const rootSelectAllLabel = localizationService.toLanguageString('custom.selectAll', enMessages.custom.selectAll);

  const [dateFilter, setDateFilter] = useState<EventFilter>(() => ({
    startDate: moment().subtract(30, 'days').startOf('day').toDate(),
    endDate: moment().add(30, 'days').endOf('day').toDate(),
  }));

  const [compliance, setCompliance] = useState<ComplianceFilters>({
    rightGrease: true,
    rightAmount: true,
    rightTime: true,
  });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortByPopupOpen, setSortByPopupOpen] = useState(false);
  const sortByAnchorRef = useRef<ButtonHandle>(null);
  const [groupBy, setGroupBy] = useState<'AssetAndSchedule' | 'Schedule'>('AssetAndSchedule');
  const [groupByPopupOpen, setGroupByPopupOpen] = useState(false);
  const groupByAnchorRef = useRef<ButtonHandle>(null);
  // Toolbar divider: show it only while the date block and controls block sit on the
  // same line; hide it once the controls wrap to a new row.
  const datesBlockRef = useRef<HTMLDivElement>(null);
  const controlsBlockRef = useRef<HTMLDivElement>(null);
  const [controlsSameLine, setControlsSameLine] = useState(true);

  useEffect(() => {
    const dates = datesBlockRef.current;
    const controls = controlsBlockRef.current;
    if (!dates || !controls) return;
    const update = () => setControlsSameLine(dates.offsetTop === controls.offsetTop);
    update();
    const ro = new ResizeObserver(update);
    if (dates.parentElement) ro.observe(dates.parentElement);
    return () => ro.disconnect();
  }, []);
  const [sortBy, setSortBy] = useState<'asset' | 'schedule'>('asset');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [isSorting, setIsSorting] = useState(false);
  const isSortingInitialRef = useRef(true);

  const [workOrdersData, setWorkOrdersData] = useState<WorkOrdersInput>([]);
  const [isGeneratingWorkOrders, setIsGeneratingWorkOrders] = useState(false);
  const [workOrdersReportReady, setWorkOrdersReportReady] = useState(false);
  const [lastGeneratedFilterKey, setLastGeneratedFilterKey] = useState<string | null>(null);
  const [reportKey, setReportKey] = useState(0);
  const [chartReportRange, setChartReportRange] = useState<{ start: Date; end: Date } | null>(null);
  const [chartCompliance, setChartCompliance] = useState<WorkOrdersComplianceSettings | null>(null);
  const [chartGroupBy, setChartGroupBy] = useState<'AssetAndSchedule' | 'Schedule'>('AssetAndSchedule');

  const filters = useWorkOrdersFilters(rootSelectAllLabel);

  // Snapshot of the filters the displayed report was generated with — restored when the panel is closed without applying.
  const appliedFiltersRef = useRef<any>(null);

  const currentFilterKey = useMemo(() => {
    const assetIds = filters.selectedLocAssetTree.map((a: any) => a.id).sort().join(',');
    const schedIds = filters.selectedSchedules.map((s: any) => s.id).sort().join(',');
    const compKey = `${compliance.rightGrease}-${compliance.rightAmount}-${compliance.rightTime}`;
    const dateKey = `${dateFilter.startDate?.toISOString()}-${dateFilter.endDate?.toISOString()}`;
    return `${assetIds}|${schedIds}|${compKey}|${dateKey}|${filters.includeUntrackedGreasing}|${groupBy}`;
  }, [filters.selectedLocAssetTree, filters.selectedSchedules, filters.includeUntrackedGreasing, compliance, dateFilter, groupBy]);

  const reportUpToDate = lastGeneratedFilterKey !== null && lastGeneratedFilterKey === currentFilterKey;

  const handleComplianceChange = useCallback((key: keyof ComplianceFilters, value: boolean) => {
    const next = { ...compliance, [key]: value };
    if (!next.rightGrease && !next.rightAmount && !next.rightTime) return;
    setCompliance(next);
  }, [compliance]);

  const handleGenerateReport = useCallback(async () => {
    if (!compliance.rightGrease && !compliance.rightAmount && !compliance.rightTime) return;

    const machineIds: string[] = [...assetIdsFromTreeSelection(filters.selectedLocAssetTree)];

    const scheduleIds = filters.selectedSchedules
      .filter((s) => s.id !== REPORT_SCHEDULE_SELECT_ALL_ROW.id && s.id !== NO_SCHEDULE_ID)
      .map((s) => String(s.id));

    const includeNoSchedule = filters.selectedSchedules.some((s) => s.id === NO_SCHEDULE_ID);

    const payload = {
      startDate: dateFilter.startDate ? moment.tz(moment(dateFilter.startDate).format('YYYY-MM-DDT00:00:00'), timezone).utc().toISOString() : undefined,
      endDate: dateFilter.endDate ? moment.tz(moment(dateFilter.endDate).format('YYYY-MM-DDT23:59:59'), timezone).utc().toISOString() : undefined,
      machineIds,
      scheduleIds,
      groupBy,
      compliance: {
        useRightGrease: compliance.rightGrease,
        useRightAmount: compliance.rightAmount,
        useRightTime: compliance.rightTime,
      },
    };

    setIsGeneratingWorkOrders(true);
    try {
      const data = await workOrdersReportApiService.fetchWorkOrdersActions(payload);
      const filtered = data
        .filter((row) => {
          const noSched = isWorkOrdersNoScheduleRow(row);
          if (scheduleIds.length === 0) {
            return includeNoSchedule && noSched;
          }
          if (noSched) return includeNoSchedule;
          return true;
        })
        // The "Untracked Greasing" row carries only unscheduled events (no compliance windows), so
        // gate it behind the toggle. Detect it by the absence of work orders rather than an empty
        // assetId — in Schedule mode real schedule rows also have an empty assetId.
        .filter((row) => filters.includeUntrackedGreasing || (row.workOrders?.length ?? 0) > 0);
      setWorkOrdersData(filtered);
      if (dateFilter.startDate && dateFilter.endDate) {
        setChartReportRange({ start: dateFilter.startDate, end: dateFilter.endDate });
      }
      setChartCompliance({ ...compliance });
      setChartGroupBy(groupBy);
      setReportKey((k) => k + 1);
      setWorkOrdersReportReady(true);
      setLastGeneratedFilterKey(currentFilterKey);
      appliedFiltersRef.current = {
        displayLocAssetTree: filters.allLocAssetTree,
        selectedLocAssetTree: filters.selectedLocAssetTree,
        displayScheduleTree: filters.allSchedules,
        selectedSchedules: filters.selectedSchedules,
        includeUntrackedGreasing: filters.includeUntrackedGreasing,
        compliance,
      };
    } catch (e) {
      console.error('Work orders report failed', e);
    } finally {
      setIsGeneratingWorkOrders(false);
    }
  }, [compliance, filters.allLocAssetTree, filters.selectedLocAssetTree, filters.allSchedules, filters.selectedSchedules, filters.includeUntrackedGreasing, dateFilter, currentFilterKey, timezone, groupBy]);

  // Closing the panel without applying discards unapplied edits: restore the snapshot the report was generated with.
  const handleCloseFilters = useCallback(() => {
    const snap = appliedFiltersRef.current;
    if (snap) {
      filters.restoreFilters(snap);
      setCompliance(snap.compliance);
    }
    setFiltersOpen(false);
  }, [filters]);

  const autoGenerateRef = useRef(true);
  useEffect(() => {
    if (!autoGenerateRef.current) return;
    if (filters.selectedLocAssetTree.length > 0 && filters.selectedSchedules.length > 0) {
      autoGenerateRef.current = false;
      handleGenerateReport();
    }
  }, [filters.selectedLocAssetTree, filters.selectedSchedules, handleGenerateReport]);

  const handleGenerateReportRef = useRef(handleGenerateReport);
  handleGenerateReportRef.current = handleGenerateReport;

  const dateFilterInitialRef = useRef(true);
  useEffect(() => {
    if (dateFilterInitialRef.current) { dateFilterInitialRef.current = false; return; }
    handleGenerateReportRef.current();
  }, [dateFilter]);

  const groupByInitialRef = useRef(true);
  useEffect(() => {
    if (groupByInitialRef.current) { groupByInitialRef.current = false; return; }
    handleGenerateReportRef.current();
  }, [groupBy]);

  useEffect(() => {
    if (isSortingInitialRef.current) { isSortingInitialRef.current = false; return; }
    setIsSorting(true);
    const t = setTimeout(() => setIsSorting(false), 900);
    return () => clearTimeout(t);
  }, [sortBy, sortDir]);

  const assetsLabel = localizationService.toLanguageString('custom.assets', enMessages.custom.assets);
  const schedulesLabel = localizationService.toLanguageString('custom.schedules', enMessages.custom.schedules);

  const sortedWorkOrdersData = useMemo(() => {
    if (!workOrdersData.length) return workOrdersData;
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    // Schedule mode has no asset dimension — sort purely by schedule name.
    if (chartGroupBy === 'Schedule') {
      return [...workOrdersData].sort((a: any, b: any) => {
        const cmp = collator.compare(a.scheduleName ?? '', b.scheduleName ?? '');
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    const pin = (row: any): number => {
      if (row.assetId == null || row.assetId === '') return 2;
      const sched = (row.scheduleName ?? '').trim().toLowerCase();
      if (!sched || sched === 'no schedule') return 1;
      return 0;
    };
    return [...workOrdersData].sort((a: any, b: any) => {
      const pinDiff = pin(a) - pin(b);
      if (pinDiff !== 0) return pinDiff;
      const aVal = (sortBy === 'asset' ? a.assetName : a.scheduleName) ?? '';
      const bVal = (sortBy === 'asset' ? b.assetName : b.scheduleName) ?? '';
      const cmp = collator.compare(aVal, bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [workOrdersData, sortBy, sortDir, chartGroupBy]);

  return (
    <Drawer expanded={false} position="end" mode="overlay" width={500}>
      {filtersOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.15)' }}
            onClick={handleCloseFilters}
          />
          <div
            className="fixed top-0 right-0 h-full z-50 shadow-xl"
            style={{ width: 'min(100vw, 500px)' }}
          >
            <WorkOrdersFilterPanelItem
              {...filters}
              compliance={compliance}
              onComplianceChange={handleComplianceChange}
              onClose={handleCloseFilters}
              onReset={() => { setLastGeneratedFilterKey(null); filters.handleResetFilters(); }}
              onGenerate={handleGenerateReport}
              generateDisabled={isGeneratingWorkOrders || reportUpToDate}
            />
          </div>
        </>
      )}
      <DrawerContent>
        <div className="m-5">
          <div className="flex items-center justify-between gap-4">
            <h2 className="gb-h2">
              {localizationService.toLanguageString('custom.workOrdersReport', enMessages.custom.workOrdersReport)}
              {timezone && <span className="ml-2 text-base font-normal text-slate-500">({timezone})</span>}
            </h2>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div ref={datesBlockRef} className="flex items-center gap-2">
            <div style={{ width: '150px' }}>
              <DatePicker
                calendar={CustomCalendar}
                max={dateFilter.endDate ?? undefined}
                value={dateFilter.startDate ?? null}
                onChange={(e) => setDateFilter((prev) => ({ ...prev, startDate: e.target.value }))}
                className="w-full"
              />
            </div>
            <span className="text-slate-400">–</span>
            <div style={{ width: '150px' }}>
              <DatePicker
                calendar={CustomCalendar}
                min={dateFilter.startDate ?? undefined}
                value={dateFilter.endDate ?? null}
                onChange={(e) => setDateFilter((prev) => ({ ...prev, endDate: e.target.value }))}
                className="w-full"
              />
            </div>
            </div>
            <div
              ref={controlsBlockRef}
              className={`flex flex-wrap items-center gap-2 ${controlsSameLine ? 'border-l border-slate-300 pl-4' : ''}`}
            >
            <Button
              ref={groupByAnchorRef}
              fillMode="outline"
              className="order-by-trigger"
              onClick={() => setGroupByPopupOpen((o) => !o)}
            >
              Group By
              <SvgIcon icon={chevronDownIcon} size="medium" className="order-by-chevron" style={{ marginLeft: 4 }} />
            </Button>
            <Popup
              anchor={groupByAnchorRef.current?.element ?? undefined}
              show={groupByPopupOpen && !!groupByAnchorRef.current?.element}
              positionMode="fixed"
              popupAlign={{ horizontal: 'left', vertical: 'top' }}
              anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
              margin={{ horizontal: 0, vertical: 4 }}
              popupClass="k-padding-md"
              onClose={() => setGroupByPopupOpen(false)}
              onMouseDownOutside={() => setGroupByPopupOpen(false)}
            >
              <div className="flex flex-col items-center gap-1 py-1" style={{ width: 150 }}>
                <Button
                  togglable
                  selected={groupBy === 'AssetAndSchedule'}
                  style={{ width: 140 }}
                  onClick={() => { setGroupBy('AssetAndSchedule'); setGroupByPopupOpen(false); }}
                >
                  {assetsLabel}
                </Button>
                <Button
                  togglable
                  selected={groupBy === 'Schedule'}
                  style={{ width: 140 }}
                  onClick={() => {
                    // Schedule grouping has no asset dimension — force the Order By to schedule.
                    setGroupBy('Schedule');
                    setSortBy('schedule');
                    setGroupByPopupOpen(false);
                  }}
                >
                  {schedulesLabel}
                </Button>
              </div>
            </Popup>
            <Button
              ref={sortByAnchorRef}
              fillMode="outline"
              className="order-by-trigger"
              onClick={() => setSortByPopupOpen((o) => !o)}
            >
              Order By
              <SvgIcon icon={chevronDownIcon} size="medium" className="order-by-chevron" style={{ marginLeft: 4 }} />
            </Button>
            <Popup
              anchor={sortByAnchorRef.current?.element ?? undefined}
              show={sortByPopupOpen && !!sortByAnchorRef.current?.element}
              positionMode="fixed"
              popupAlign={{ horizontal: 'left', vertical: 'top' }}
              anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
              margin={{ horizontal: 0, vertical: 4 }}
              popupClass="k-padding-md"
              onClose={() => setSortByPopupOpen(false)}
              onMouseDownOutside={() => setSortByPopupOpen(false)}
            >
              <div className="flex flex-col items-center gap-1 py-1" style={{ width: 150 }}>
                {groupBy !== 'Schedule' && (
                  <Button
                    togglable
                    selected={sortBy === 'asset'}
                    style={{ width: 140 }}
                    onClick={() => {
                      if (sortBy === 'asset') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                      else { setSortBy('asset'); setSortDir('asc'); }
                    }}
                  >
                    {assetsLabel}{sortBy === 'asset' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </Button>
                )}
                <Button
                  togglable
                  selected={sortBy === 'schedule'}
                  style={{ width: 140 }}
                  onClick={() => {
                    if (sortBy === 'schedule') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else { setSortBy('schedule'); setSortDir('asc'); }
                  }}
                >
                  {schedulesLabel}{sortBy === 'schedule' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                </Button>
              </div>
            </Popup>
            <Button
              svgIcon={arrowRotateCwIcon}
              themeColor="base"
              disabled={isGeneratingWorkOrders}
              onClick={handleGenerateReport}
              title="Refresh"
            />
            <Button themeColor="primary" svgIcon={filterIcon} onClick={() => setFiltersOpen(true)}>
              Filters
            </Button>
            </div>
          </div>

          <WorkOrdersLegend className="mb-2 mt-3" />

          <div className="appContainer relative" style={{ position: 'relative' }}>
            {!workOrdersReportReady && (
              <div className="flex min-h-[240px] items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            )}

            {workOrdersReportReady && sortedWorkOrdersData.length === 0 && (
              <div className="gb-text-normal text-lg text-center"
                style={{ width: '400px', borderRadius: '3px', padding: '40px 25px', margin: '30px auto' }}>
                <div className="text-center mb-5">
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '70px', height: '70px' }}>
                    <SvgIcon icon={graphIcon} size="xxxlarge" />
                  </span>
                </div>
                <div>
                  <span>{localizationService.toLanguageString('custom.setFiltersText', enMessages.custom.setFiltersText)}</span>
                  <ul className="mt-5">
                    <li>
                      <Button svgIcon={filterIcon} className="h-8" themeColor="primary" onClick={() => setFiltersOpen(true)}>
                        Filters
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {workOrdersReportReady && sortedWorkOrdersData.length > 0 && (
              <>
                {isSorting && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                    <Loader size="large" type="pulsing" />
                  </div>
                )}
                <WorkOrders
                  key={`${reportKey}-${sortBy}-${sortDir}-${chartGroupBy}`}
                  data={sortedWorkOrdersData}
                  loading={isGeneratingWorkOrders}
                  reportRangeStart={chartReportRange?.start ?? null}
                  reportRangeEnd={chartReportRange?.end ?? null}
                  compliance={chartCompliance ?? undefined}
                  groupBy={chartGroupBy}
                />
              </>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
